import React, { useState } from "react";
import {
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  Tabs,
  Tab,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Divider,
  Alert,
  Select,
  Checkbox,
  ListItemText,
} from "@mui/material";
import {
  ArrowBack,
  Description,
  Code,
  UploadFile,
  Add,
  Delete,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import PageTitle from "../components/common/PageTitle";
import { useBookmarkableTab } from "../hooks/useBookmarkableTab";

const CREATE_PRODUCT_TAB_SLUGS = ["overview", "api-specs"] as const;

/* -----------------------------
   Types
------------------------------ */
type CategoryOption = {
  id: string;
  name: string;
};

type StatusType = "Active" | "Inactive";

type EditableFaq = {
  id: string;
  question: string;
  answer: string;
};

/* -----------------------------
   Options
------------------------------ */
const CATEGORY_OPTIONS: CategoryOption[] = [
  { id: "finance", name: "Finance" },
  { id: "payments", name: "Payments" },
  { id: "analytics", name: "Analytics" },
  { id: "identity", name: "Identity" },
  { id: "core", name: "Core Platform" },
];

const TAG_OPTIONS = [
  "Payment",
  "Gateway",
  "API",
  "Fintech",
  "Processing",
  "Transaction",
];

/* -----------------------------
   Initial state
------------------------------ */
const EMPTY_FORM = {
  name: "",
  description: "",
  status: "Inactive" as StatusType,
  categories: [] as CategoryOption[],
  tags: [] as string[],
  faqs: [] as EditableFaq[],
  apiFiles: [] as File[],
};

const CreateProductPage: React.FC = () => {
  const navigate = useNavigate();
  const { tabIndex: tab, setTabIndex: setTab } = useBookmarkableTab({
    slugs: CREATE_PRODUCT_TAB_SLUGS,
  });
  const [form, setForm] = useState(EMPTY_FORM);

  const update = <K extends keyof typeof form>(
    key: K,
    value: (typeof form)[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const isValid = form.name.trim().length > 0 && form.categories.length > 0;

  const handleSubmit = () => {
    console.log("Create product payload:", form);
    navigate("/product-catalog");
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* HEADER */}
      <PageTitle
        pageTitle="Create New Product"
        pageDescription="Fill out the Inputs below to create a New Product"
      />
      {/* New Product Name */}
      <br/>
      <Box>
        <Container maxWidth="lg">
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton onClick={() => navigate("/product-catalog")}>
              <ArrowBack />
            </IconButton>

            <TextField
              variant="standard"
              placeholder="New product name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              sx={{ ml: 2, flex: 1 }}
              InputProps={{
                sx: { fontSize: "1.75rem", fontWeight: 700 },
              }}
            />

            <Button
              variant="contained"
              sx={{ ml: 2 }}
              disabled={!isValid}
              onClick={handleSubmit}
            >
              Create Product
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={0}>
          <Tabs value={tab} onChange={setTab}>
            <Tab icon={<Description />} iconPosition="start" label="Overview" />
            <Tab icon={<Code />} iconPosition="start" label="API Specs" />
          </Tabs>

          <Box sx={{ p: 4 }}>
            {/* OVERVIEW TAB */}
            {tab === 0 && (
              <Grid container spacing={4}>
                {/* LEFT COLUMN */}
                <Grid item xs={12} md={8}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                    Description
                  </Typography>

                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    placeholder="Describe this product"
                    value={form.description}
                    onChange={(e) => update("description", e.target.value)}
                  />

                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                      Product Information
                    </Typography>

                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          select
                          fullWidth
                          required
                          label="Category"
                          value={form.categories[0]?.id || ""}
                          onChange={(e) => {
                            const selected = CATEGORY_OPTIONS.find(
                              (c) => c.id === e.target.value,
                            );
                            update("categories", selected ? [selected] : []);
                          }}
                        >
                          {CATEGORY_OPTIONS.map((category) => (
                            <MenuItem key={category.id} value={category.id}>
                              {category.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          select
                          fullWidth
                          label="Status"
                          value={form.status}
                          onChange={(e) =>
                            update("status", e.target.value as StatusType)
                          }
                        >
                          <MenuItem value="Active">Active</MenuItem>
                          <MenuItem value="Inactive">Inactive</MenuItem>
                        </TextField>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                      Frequently Asked Questions
                    </Typography>

                    {form.faqs.length === 0 && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        No FAQs added yet.
                      </Alert>
                    )}

                    {form.faqs.map((faq, index) => (
                      <Paper
                        key={faq.id}
                        elevation={0}
                        sx={{
                          p: 2,
                          mb: 2,
                          border: "1px solid",
                          borderColor: "grey.200",
                        }}
                      >
                        <TextField
                          fullWidth
                          label="Question"
                          value={faq.question}
                          sx={{ mb: 2 }}
                          onChange={(e) => {
                            const updated = [...form.faqs];
                            updated[index] = {
                              ...faq,
                              question: e.target.value,
                            };
                            update("faqs", updated);
                          }}
                        />

                        <TextField
                          fullWidth
                          multiline
                          minRows={3}
                          label="Answer"
                          value={faq.answer}
                          onChange={(e) => {
                            const updated = [...form.faqs];
                            updated[index] = {
                              ...faq,
                              answer: e.target.value,
                            };
                            update("faqs", updated);
                          }}
                        />

                        <Box sx={{ textAlign: "right", mt: 1 }}>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<Delete />}
                            onClick={() =>
                              update(
                                "faqs",
                                form.faqs.filter((_, i) => i !== index),
                              )
                            }
                          >
                            Remove
                          </Button>
                        </Box>
                      </Paper>
                    ))}

                    <Button
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={() =>
                        update("faqs", [
                          ...form.faqs,
                          {
                            id: crypto.randomUUID(),
                            question: "",
                            answer: "",
                          },
                        ])
                      }
                    >
                      Add FAQ
                    </Button>
                  </Box>
                </Grid>

                {/* RIGHT COLUMN */}
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={0}
                    sx={{ p: 3, border: "1px solid", borderColor: "grey.200" }}
                  >
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                      Tags
                    </Typography>

                    <Select
                      fullWidth
                      multiple
                      value={form.tags}
                      onChange={(e) =>
                        update("tags", e.target.value as string[])
                      }
                      renderValue={(selected) =>
                        (selected as string[]).join(", ")
                      }
                    >
                      {TAG_OPTIONS.map((tag) => (
                        <MenuItem key={tag} value={tag}>
                          <Checkbox checked={form.tags.includes(tag)} />
                          <ListItemText primary={tag} />
                        </MenuItem>
                      ))}
                    </Select>

                    <Divider sx={{ my: 3 }} />

                    <Alert severity="info">
                      Select tags to improve product discoverability.
                    </Alert>
                  </Paper>
                </Grid>
              </Grid>
            )}

            {/* API TAB */}
            {tab === 1 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                  API Files
                </Typography>

                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadFile />}
                >
                  Upload API Files
                  <input
                    hidden
                    multiple
                    type="file"
                    onChange={(e) =>
                      update(
                        "apiFiles",
                        e.target.files ? Array.from(e.target.files) : [],
                      )
                    }
                  />
                </Button>

                {form.apiFiles.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    {form.apiFiles.map((file, i) => (
                      <Typography key={i} variant="body2">
                        • {file.name}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default CreateProductPage;
